import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSales, useDeleteSale, useSetSaleRefunded, saleNetRevenue } from '../../hooks/useSales'
import { formatMoney, formatDate, monthKey } from '../../lib/format'
import type { Sale } from '../../lib/database.types'
import { Skeleton, SwipeRow, Chip } from '../../components/ui'
import ConsigneeBadge from '../../components/ConsigneeBadge'

type SalesFilter = 'all' | 'mine' | 'consignee'

export default function SalesList() {
  const { data: sales, isLoading } = useSales()
  const deleteSale = useDeleteSale()
  const setRefunded = useSetSaleRefunded()
  const navigate = useNavigate()
  const [filter, setFilter] = useState<SalesFilter>('all')

  const toggleRefund = (sale: Sale) => {
    const next = !sale.is_refunded
    const verb = next ? 'refund' : 'undo this refund'
    if (window.confirm(`Are you sure you want to ${verb}? Units will ${next ? 'return to' : 'leave'} inventory.`)) {
      setRefunded.mutate({ id: sale.id, refunded: next })
    }
  }

  if (isLoading) return (
    <div>
      {Array.from({ length: 5 }, (_, i) => (
        <Skeleton key={i} height={60} style={{ marginBottom: 8 }} />
      ))}
    </div>
  )

  const filteredSales = (sales ?? []).filter((s) => {
    if (filter === 'all') return true
    if (filter === 'mine') return s.consignee_id == null
    return s.consignee_id != null
  })
  const groups = groupByMonth(filteredSales)

  return (
    <div>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 'var(--space-2)',
        marginBottom: 'var(--space-4)',
        flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          {([
            { value: 'all', label: 'All' },
            { value: 'mine', label: 'By me' },
            { value: 'consignee', label: 'By consignee' },
          ] as { value: SalesFilter; label: string }[]).map((opt) => {
            const active = filter === opt.value
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setFilter(opt.value)}
                aria-pressed={active}
                style={{
                  padding: 'var(--space-1) var(--space-3)',
                  borderRadius: 'var(--radius-pill)',
                  border: `1px solid ${active ? 'var(--color-primary)' : 'var(--color-border)'}`,
                  background: active ? 'var(--color-primary-soft)' : 'transparent',
                  color: active ? 'var(--color-primary-text)' : 'var(--color-text-muted)',
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: active ? 700 : 500,
                  cursor: 'pointer',
                }}
              >
                {opt.label}
              </button>
            )
          })}
        </div>
        <button
          type="button"
          onClick={() => navigate('/money/sales/new')}
          style={{
            padding: 'var(--space-2) var(--space-4)',
            background: 'var(--color-primary)',
            color: 'white',
            borderRadius: 'var(--radius-md)',
            fontWeight: 600,
            fontSize: 'var(--font-size-sm)',
            border: 'none',
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          + Log sale
        </button>
      </div>

      {Object.keys(groups).length === 0 ? (
        <div style={{ padding: 'var(--space-8)', textAlign: 'center', background: 'var(--color-surface)', borderRadius: 'var(--radius-lg)', color: 'var(--color-text-muted)' }}>
          No sales yet — tap &quot;Log sale&quot; to add your first.
        </div>
      ) : (
        <div
          style={{
            maxHeight: '70vh',
            overflowY: 'auto',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-3)',
            background: 'var(--color-bg)',
            // iOS-style momentum scroll inside the container
            WebkitOverflowScrolling: 'touch',
          }}
        >
        {Object.entries(groups)
          .sort(([a], [b]) => b.localeCompare(a))
          .map(([month, list]) => {
            const liveSales = list.filter((s) => !s.is_refunded)
            const totalGross = liveSales.reduce((sum, s) => sum + Number(s.gross_amount), 0)
            const totalNet = liveSales.reduce((sum, s) => sum + saleNetRevenue(s), 0)
            const refundedCount = list.length - liveSales.length
            return (
              <div key={month} style={{ marginBottom: 'var(--space-6)' }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: 'var(--space-2) var(--space-3)',
                  background: 'var(--color-surface-2)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  marginBottom: 'var(--space-2)',
                }}>
                  <span>
                    {monthLabel(month)}
                    {refundedCount > 0 && (
                      <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)', fontWeight: 400, marginLeft: 'var(--space-2)' }}>
                        ({refundedCount} refunded)
                      </span>
                    )}
                  </span>
                  <span>{formatMoney(totalGross)} gross · {formatMoney(totalNet)} net</span>
                </div>
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {list.map((s) => {
                    const isRefunded = s.is_refunded
                    return (
                      <li key={s.id} style={{ marginBottom: 'var(--space-2)' }}>
                        <SwipeRow
                          onEdit={() => navigate(`/money/sales/${s.id}/edit`)}
                          onDelete={() => deleteSale.mutate(s.id)}
                          deleteConfirmText="Delete this sale?"
                        >
                          <div style={{
                            background: 'var(--color-surface)',
                            padding: 'var(--space-3)',
                            borderRadius: 'var(--radius-md)',
                            opacity: isRefunded ? 0.6 : 1,
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
                                  <span style={{ fontWeight: 600, textDecoration: isRefunded ? 'line-through' : 'none' }}>
                                    {s.platform}
                                  </span>
                                  {isRefunded && <Chip tone="danger">REFUNDED</Chip>}
                                  <ConsigneeBadge consigneeId={s.consignee_id} />
                                </div>
                                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)' }}>
                                  {formatDate(s.sale_date)}{s.customer_name && ` · ${s.customer_name}`}
                                </div>
                              </div>
                              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                <div style={{
                                  fontWeight: 700,
                                  textDecoration: isRefunded ? 'line-through' : 'none',
                                }}>
                                  {formatMoney(Number(s.gross_amount))}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => toggleRefund(s)}
                                  style={{
                                    marginTop: 4,
                                    fontSize: 'var(--font-size-xs)',
                                    color: isRefunded ? 'var(--color-success-text)' : 'var(--color-warning-text)',
                                    fontWeight: 600,
                                    background: 'transparent',
                                  }}
                                  disabled={setRefunded.isPending}
                                >
                                  {isRefunded ? '↺ Undo refund' : 'Refund'}
                                </button>
                              </div>
                            </div>
                          </div>
                        </SwipeRow>
                      </li>
                    )
                  })}
                </ul>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function groupByMonth(sales: Sale[]): Record<string, Sale[]> {
  const groups: Record<string, Sale[]> = {}
  for (const s of sales) {
    const key = monthKey(s.sale_date)
    if (!groups[key]) groups[key] = []
    groups[key].push(s)
  }
  return groups
}

function monthLabel(key: string): string {
  const [year, month] = key.split('-')
  const date = new Date(Number(year), Number(month) - 1, 1)
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
}
